// src/App.jsx
import { useState, useEffect, useCallback } from 'react';
import { AlertCircle, LogOut, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { getCsrfToken, resetCsrfToken } from './utils/csrfToken';
import { log, sanitizeString, getDisplayError, isValidUrl } from './utils/helpers';
import BalanceCard from './components/BalanceCard';
import EventsList from './components/EventsList';
import PaymentHistory from './components/PaymentHistory';
import AdminModals from './components/AdminModals';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function EventPaymentDashboard() {
  // Core state
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // UI state
  const [expandedSections, setExpandedSections] = useState({
    payments: false  // Only payment history is collapsible
  });
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [loadingPaymentUrl, setLoadingPaymentUrl] = useState(false);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);

  // Admin modal state
  const [showReports, setShowReports] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showCreditBalances, setShowCreditBalances] = useState(false);
  const [showLookupMember, setShowLookupMember] = useState(false);
  const [showVisitStats, setShowVisitStats] = useState(false);
  const [showMemberView, setShowMemberView] = useState(false);

  const checkAuthStatus = useCallback(async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlError = urlParams.get('error');
      
      if (urlError) {
        const safeError = sanitizeString(urlError);
        log('Auth error from URL:', safeError);
        setError(getDisplayError(safeError, 'auth'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/auth/status`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        throw new Error(`Status ${response.status}`);
      }
      
      const data = await response.json();
      log('Auth status:', data);
      
      if (data.authenticated) {
        setIsAuthenticated(true);
        loadDashboardData();
      } else {
        setIsAuthenticated(false);
        setLoading(false);
      }
    } catch (err) {
      log('Auth status check failed:', err.message);
      setError(getDisplayError(err, 'auth'));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showAdminDropdown && !e.target.closest('[data-admin-dropdown]')) {
        setShowAdminDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showAdminDropdown]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/dashboard`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        throw new Error(`Failed to load dashboard (${response.status})`);
      }
      
      const data = await response.json();
      setDashboardData(data);
      setError(null);
      
      try {
        const adminResponse = await fetch(`${API_URL}/api/admin/status`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (adminResponse.ok) {
          const adminData = await adminResponse.json();
          setIsAdmin(adminData.isAdmin);
        }
      } catch (adminErr) {
        log('Admin status check failed:', adminErr.message);
        setIsAdmin(false);
      }
    } catch (err) {
      log('Dashboard load failed:', err.message);
      setError(getDisplayError(err, 'dashboard'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    window.location.href = `${API_URL}/auth/login`;
  };

  const handleLogout = async () => {
    try {
      const token = await getCsrfToken();
      
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        }
      });
    } catch (err) {
      log('Logout error:', err.message);
      setError(getDisplayError(err, 'logout'));
    }
    
    setIsAuthenticated(false);
    setDashboardData(null);
    resetCsrfToken();
  };

  const handleGetPaymentUrl = async () => {
    setLoadingPaymentUrl(true);
    try {
      const token = await getCsrfToken();
      
      const response = await fetch(`${API_URL}/api/payment-link`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get payment link (${response.status})`);
      }
      
      const data = await response.json();
      
      if (!data.paymentUrl || !isValidUrl(data.paymentUrl)) {
        throw new Error('Invalid payment URL received');
      }
      setPaymentUrl(data.paymentUrl);
      window.open(data.paymentUrl, '_self');
      
    } catch (err) {
      log('Payment URL fetch failed:', err.message);
      setError(getDisplayError(err, 'payment'));
    } finally {
      setLoadingPaymentUrl(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-start justify-center pt-20 md:pt-32 md:items-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <img src="/all-nations-logo.png" alt="All Nations Breakthrough Church" className="h-20 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">CBA Payment Tracker</h1>
          <p className="text-gray-600 mb-6">Log in with your church app login.</p>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <button
            onClick={handleLogin}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all font-medium w-full shadow-lg"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={loadDashboardData}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-xl hover:bg-gray-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) return null;

  // Not in group
  if (dashboardData.notInGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Not Enrolled</h2>
          <p className="text-gray-600 mb-6">{sanitizeString(dashboardData.message)}</p>
          <button
            onClick={handleLogout}
            className="bg-gray-600 text-white px-6 py-2 rounded-xl hover:bg-gray-700 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">

          {/* Mobile Layout */}
          <div className="flex items-center justify-between md:hidden">
            <div className="flex items-center gap-2">
              <img src="/all-nations-logo.png" alt="ANBC" className="h-7 flex-shrink-0" />
              {isAdmin && <span className="text-sm font-medium text-gray-800">CBA</span>}
            </div>
            
            {!isAdmin && (
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium text-gray-500">CBA</span>
                <span className="text-sm font-medium text-gray-800">Dashboard</span>
              </div>
            )}
            
            <div className={`flex items-center gap-2 ${!isAdmin ? 'justify-end' : ''}`}>
              {paymentUrl ? (
                <a 
                  href={paymentUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 font-medium text-sm hover:underline cursor-pointer"
                >
                  {isAdmin ? 'Pay' : 'Payment'}
                </a>
              ) : (
                <button
                  onClick={handleGetPaymentUrl}
                  disabled={loadingPaymentUrl}
                  className="text-gray-600 font-medium text-sm hover:underline cursor-pointer disabled:opacity-50"
                >
                  {loadingPaymentUrl ? 'Loading...' : (isAdmin ? 'Pay' : 'Payment')}
                </button>
              )}
              {isAdmin && (
                <div className="relative" data-admin-dropdown>
                  <button 
                    onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                    className="text-gray-600 font-medium text-sm hover:underline cursor-pointer flex items-center gap-1"
                  >
                    Admin
                    {showAdminDropdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showAdminDropdown && (
                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-40">
                      <button
                        onClick={() => {
                          setShowReports(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Reports
                      </button>
                      <button
                        onClick={() => {
                          setShowLookupMember(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Lookup Member
                      </button>
                      <button
                        onClick={() => {
                          setShowMemberView(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Member View
                      </button>
                      <button
                        onClick={() => {
                          setShowAdminPanel(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Resync Payments
                      </button>
                      <button
                        onClick={() => {
                          setShowCreditBalances(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Credit Balances
                      </button>
                      <button
                        onClick={() => {
                          setShowVisitStats(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Visit Stats
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={loadDashboardData}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:grid grid-cols-3 items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/all-nations-logo.png" alt="ANBC" className="h-12" />
            </div>
            
            <div className="text-center">
              <h1 className="text-lg font-bold text-gray-800">CBA Dashboard</h1>
              <p className="text-sm text-gray-600">ANBC</p>
            </div>
            
            <div className="flex items-center justify-end gap-3">
              {paymentUrl ? (
                <a 
                  href={paymentUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 font-medium hover:underline cursor-pointer"
                >
                  Pay
                </a>
              ) : (
                <button
                  onClick={handleGetPaymentUrl}
                  disabled={loadingPaymentUrl}
                  className="text-gray-600 font-medium hover:underline cursor-pointer disabled:opacity-50"
                >
                  {loadingPaymentUrl ? 'Loading...' : 'Pay'}
                </button>
              )}
              {isAdmin && (
                <div className="relative" data-admin-dropdown>
                  <button 
                    onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                    className="text-gray-600 font-medium hover:underline cursor-pointer flex items-center gap-1"
                  >
                    Admin
                    {showAdminDropdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {showAdminDropdown && (
                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-40">
                      <button
                        onClick={() => {
                          setShowReports(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Reports
                      </button>
                      <button
                        onClick={() => {
                          setShowLookupMember(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Lookup Member
                      </button>
                      <button
                        onClick={() => {
                          setShowMemberView(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Member View
                      </button>
                      <button
                        onClick={() => {
                          setShowAdminPanel(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Resync Payments
                      </button>
                      <button
                        onClick={() => {
                          setShowCreditBalances(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200"
                      >
                        Credit Balances
                      </button>
                      <button
                        onClick={() => {
                          setShowVisitStats(true);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Visit Stats
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={loadDashboardData}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Balance Card Component */}
        <BalanceCard dashboardData={dashboardData} />

        {/* Events List Component */}
        <EventsList 
          currentEvents={dashboardData.currentEvents}
          historicalEvents={dashboardData.historicalEvents}
          requiredAmount={dashboardData.requiredAmount}
          startingBalance={dashboardData.startingBalance}
        />

        {/* Payment History Component */}
        <PaymentHistory 
          paymentHistory={dashboardData.paymentHistory}
          isExpanded={expandedSections.payments}
          onToggle={() => toggleSection('payments')}
        />

        {/* Info Footer */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-2">How it works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Current occurrence will be due on due date shown.</li>
                <li>Credit bal is amount you prepaid.</li>
                <li>Prepaid bal will apply towards current occurrence on due date.</li>
                <li>Payments are applied in order (first-in, first-out).</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Modals Component */}
      <AdminModals 
        showReports={showReports}
        setShowReports={setShowReports}
        showAdminPanel={showAdminPanel}
        setShowAdminPanel={setShowAdminPanel}
        showCreditBalances={showCreditBalances}
        setShowCreditBalances={setShowCreditBalances}
        showLookupMember={showLookupMember}
        setShowLookupMember={setShowLookupMember}
        showVisitStats={showVisitStats}
        setShowVisitStats={setShowVisitStats}
        showMemberView={showMemberView}
        setShowMemberView={setShowMemberView}
      />
    </div>
  );
}